import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, errorMessage, primaryButton, secondaryButton } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

interface LeaveType {
  id: string;
  name: string;
  annualDays: number | null;
  isPaid: boolean;
  isActive: boolean;
}
export interface LeaveRequest {
  id: string;
  staffName: string;
  leaveTypeName: string;
  fromDate: string;
  toDate: string;
  days: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reviewNote: string | null;
}

export function LeaveTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [typeForm, setTypeForm] = useState({ name: '', annualDays: '', isPaid: true });
  const [typeError, setTypeError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const types = useQuery({
    queryKey: ['hr', schoolId, 'leave-types'],
    queryFn: () => api<LeaveType[]>(`/schools/${schoolId}/leave-types`),
  });
  const requests = useQuery({
    queryKey: ['hr', schoolId, 'leave'],
    queryFn: () =>
      apiList<LeaveRequest>(`/schools/${schoolId}/staff-leave`, { query: { pageSize: 50 } }).then(
        (r) => r.data,
      ),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['hr', schoolId] });
  const addType = useMutation({
    mutationFn: (body: object) => api(`/schools/${schoolId}/leave-types`, { method: 'POST', body }),
    onSuccess: () => {
      setTypeForm({ name: '', annualDays: '', isPaid: true });
      setMessage(null);
      refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const review = useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected' }) =>
      api(`/schools/${schoolId}/staff-leave/${args.id}/review`, {
        method: 'POST',
        body: { decision: args.decision, note: notes[args.id] || null },
      }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });

  return (
    <section aria-label={t('hr.tab.leave')} className="space-y-6">
      {message && <Notice kind="error">{message}</Notice>}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('hr.leaveTypes')}</h2>
        <ul className="flex flex-wrap gap-2 text-sm">
          {types.data?.map((lt) => (
            <li key={lt.id} className="rounded border border-slate-200 bg-white px-3 py-1">
              {lt.name} · {lt.annualDays === null ? '∞' : lt.annualDays} ·{' '}
              {lt.isPaid ? t('hr.paid') : '—'}
            </li>
          ))}
        </ul>
        {can('hr_leave.manage_types', schoolId) && (
          <form
            noValidate
            aria-label={t('hr.addLeaveType')}
            onSubmit={(e) => {
              e.preventDefault();
              const days = typeForm.annualDays.trim() === '' ? null : Number(typeForm.annualDays);
              if (
                !typeForm.name.trim() ||
                (days !== null &&
                  (!Number.isFinite(days) ||
                    days < 0 ||
                    days > 366 ||
                    days * 2 !== Math.round(days * 2)))
              )
                return setTypeError(t('hr.annualDays'));
              setTypeError(null);
              addType.mutate({
                name: typeForm.name.trim(),
                annualDays: days,
                isPaid: typeForm.isPaid,
              });
            }}
            className="grid items-end gap-4 sm:grid-cols-4"
          >
            <TextField
              label={t('hr.leaveTypeName')}
              value={typeForm.name}
              onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
            />
            <TextField
              label={t('hr.annualDays')}
              inputMode="decimal"
              value={typeForm.annualDays}
              error={typeError}
              onChange={(e) => setTypeForm({ ...typeForm, annualDays: e.target.value })}
            />
            <label className="flex items-center gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                checked={typeForm.isPaid}
                onChange={(e) => setTypeForm({ ...typeForm, isPaid: e.target.checked })}
              />
              {t('hr.paid')}
            </label>
            <button type="submit" className={primaryButton} disabled={addType.isPending}>
              {t('hr.addLeaveType')}
            </button>
          </form>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('hr.requests')}</h2>
        {requests.data?.length === 0 && <p className="text-slate-600">{t('hr.noRequests')}</p>}
        <ul className="space-y-3">
          {requests.data?.map((r) => (
            <li key={r.id} className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold">
                  {r.staffName} · {r.leaveTypeName}
                </span>
                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium">
                  {t(`hr.requestStatus.${r.status}` as MessageKey)}
                </span>
              </div>
              <p className="mt-1 text-slate-700">
                {r.fromDate === r.toDate ? r.fromDate : `${r.fromDate} → ${r.toDate}`} ·{' '}
                {t('hr.days', { days: r.days })} — {r.reason}
              </p>
              {r.reviewNote && <p className="text-slate-600">{r.reviewNote}</p>}
              {r.status === 'pending' && can('hr_leave.review', schoolId) && (
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <input
                    aria-label={`${t('hr.cancelReason')}: ${r.staffName}`}
                    placeholder={t('hr.cancelReason')}
                    value={notes[r.id] ?? ''}
                    onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                    className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('hr.approve')}: ${r.staffName}`}
                    disabled={review.isPending}
                    onClick={() => review.mutate({ id: r.id, decision: 'approved' })}
                  >
                    {t('hr.approve')}
                  </button>
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('hr.reject')}: ${r.staffName}`}
                    disabled={review.isPending}
                    onClick={() => review.mutate({ id: r.id, decision: 'rejected' })}
                  >
                    {t('hr.reject')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
